/* @flow */

import * as React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

// Styles
import styles from 'src/shared/app/base/component/data-entry/form-builder/FormBuilder.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Utils
import messages from 'src/shared/app/base/component/data-entry/form-builder/l10n/formBuilderL10n';
import { useField } from 'formik';
import { getFieldTitle } from 'src/shared/app/base/component/data-entry/form-builder/utils/FormBuilderUtils';
import { useId, useVisuallyHidden } from 'react-aria';
import {
  HAPPY,
  SMILE,
  NEUTRAL,
  SAD,
  DIZZY,
} from 'src/shared/ui/component/icon/smileys/smileyVariants';

// Components
import Smiley from 'src/shared/ui/component/icon/smileys/Smiley';

// Types
import type { Field } from 'src/shared/app/base/component/data-entry/form-builder/type/formBuilderTypes';

const useStyles = createUseThemeStyles(styles);

const FormBuilderRating = ({ field, name }: Field): React.Node => {
  const intl = useIntl();
  const labelId = useId();
  const classes = useStyles();

  const { visuallyHiddenProps } = useVisuallyHidden();
  const [{ value: activeRating }, , { setValue }] = useField(name);

  return (
    <>
      <label id={labelId}>{getFieldTitle(field, intl)}</label>

      <div
        aria-labelledby={labelId}
        role="radiogroup"
        className={classes.ratings}
      >
        {[DIZZY, SAD, NEUTRAL, SMILE, HAPPY].map((variant, key) => {
          const value = key + 1;
          const isActive = activeRating === value;

          const id = `${name}-${variant}`;

          return (
            <label
              htmlFor={id}
              className={classNames(classes.ratingButton)}
              aria-label={intl.formatMessage(
                messages[`feedbackEmotion-${variant}`],
              )}
              key={key}
            >
              <input
                type="radio"
                value={variant}
                id={id}
                name={name}
                checked={isActive}
                onChange={() => setValue(value)}
                {...visuallyHiddenProps}
              />
              <Smiley variant={variant} isActive={isActive} />
            </label>
          );
        })}
      </div>
    </>
  );
};

export default React.memo<Field>(FormBuilderRating);
